import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Send, Trash2 } from 'lucide-react';
import { commentsApi } from '@/lib/commentsApi';
import { useToast, useConfirm } from '@/components/ui';
import { useCurrentUser } from '@/hooks/useAuth';

interface Props {
  workspaceId: string;
  targetKind: 'contact' | 'account' | 'deal' | 'signal' | 'sequence';
  targetId: string;
}

/**
 * Drop-in comment thread for any drawer / detail panel. Lists
 * existing comments, surfaces @<uuid> mentions as inline tagged
 * chips, and posts new comments via the workspace-scoped endpoint.
 *
 * Single mutation toast on post success/failure — fits the toast
 * policy (server mutation result, never on navigation).
 */
export function CommentThread({ workspaceId, targetKind, targetId }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const me = useCurrentUser();
  const [body, setBody] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['comments', workspaceId, targetKind, targetId] });

  const list = useQuery({
    queryKey: ['comments', workspaceId, targetKind, targetId],
    queryFn: () => commentsApi.list(workspaceId, targetKind, targetId),
    enabled: Boolean(workspaceId && targetId),
  });

  const post = useMutation({
    mutationFn: () => commentsApi.create(workspaceId, { targetKind, targetId, body: body.trim() }),
    onSuccess: () => {
      invalidate();
      setBody('');
      toast.success(t('comments.posted'));
    },
    onError: () => toast.error(t('comments.post_failed')),
  });

  const edit = useMutation({
    mutationFn: (vars: { id: string; body: string }) =>
      commentsApi.update(workspaceId, vars.id, vars.body),
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      toast.success(t('comments.edited'));
    },
    onError: () => toast.error(t('common.save_failed')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => commentsApi.remove(workspaceId, id),
    onSuccess: () => {
      invalidate();
      toast.success(t('comments.deleted'));
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  const askDelete = async (id: string) => {
    const ok = await confirm({
      title: t('comments.confirm_delete_title'),
      description: t('comments.confirm_delete_body'),
      confirmLabel: t('comments.delete'),
      variant: 'danger',
    });
    if (ok) remove.mutate(id);
  };

  const rows = list.data ?? [];
  const myId = me.data?.id;

  function renderBodyWithMentions(text: string) {
    // Mentions are `@<uuid>` — render as kind-tinted chips.
    const parts: React.ReactNode[] = [];
    const re = /@([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/g;
    let last = 0;
    let m: RegExpExecArray | null;
    let i = 0;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) parts.push(<span key={`t${i++}`}>{text.slice(last, m.index)}</span>);
      parts.push(
        <span
          key={`m${i++}`}
          className="inline-flex items-center px-1.5 py-0.5 rounded bg-accent-50 text-accent-700 text-[11px] font-semibold num"
        >
          @{m[1].slice(0, 8)}
        </span>,
      );
      last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(<span key={`t${i++}`}>{text.slice(last)}</span>);
    return <>{parts}</>;
  }

  return (
    <section className="space-y-3">
      <h3 className="text-xs uppercase tracking-[0.16em] text-ink-500 font-semibold">
        {t('comments.title')}
      </h3>

      {list.isLoading && <p className="text-xs text-ink-500">{t('common.loading')}</p>}

      {!list.isLoading && rows.length === 0 && (
        <p className="text-xs text-ink-500 italic">{t('comments.empty')}</p>
      )}

      {rows.length > 0 && (
        <ul className="space-y-3">
          {rows.map((c) => {
            const mine = !!myId && c.authorUserId === myId;
            const isEditing = editingId === c.id;
            return (
              <li key={c.id} className="group border-b border-ink-100 pb-3 last:border-b-0 last:pb-0">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="num text-[11px] text-ink-500">
                    @{(c.authorUserId ?? '').slice(0, 8)}
                  </span>
                  <div className="flex items-center gap-2">
                    {mine && !isEditing && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(c.id);
                            setEditBody(c.body);
                          }}
                          aria-label={t('comments.edit')}
                          title={t('comments.edit')}
                          className="rounded p-0.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => askDelete(c.id)}
                          aria-label={t('comments.delete')}
                          title={t('comments.delete')}
                          className="rounded p-0.5 text-warn-500 hover:bg-warn-500/10"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    <span className="num text-[11px] text-ink-400">
                      {new Date(c.createdAt).toLocaleString()}
                    </span>
                  </div>
                </div>
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={2}
                      className="w-full rounded-md border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="text-xs px-2.5 py-1 rounded-md text-ink-600 hover:bg-ink-100"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={() => edit.mutate({ id: c.id, body: editBody.trim() })}
                        disabled={!editBody.trim() || edit.isPending}
                        className="text-xs px-2.5 py-1 rounded-md bg-ink-900 text-white font-semibold hover:bg-ink-800 disabled:opacity-60"
                      >
                        {edit.isPending ? t('common.saving') : t('comments.save')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-ink-900 leading-relaxed">
                    {renderBodyWithMentions(c.body)}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="border-t border-ink-200 pt-3">
        <label className="block">
          <span className="sr-only">{t('comments.placeholder')}</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('comments.placeholder')}
            rows={2}
            className="w-full rounded-md border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
        </label>
        <p className="text-[11px] text-ink-500 mt-1">{t('comments.mention_hint')}</p>
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => post.mutate()}
            disabled={!body.trim() || post.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-ink-900 text-white text-sm font-semibold hover:bg-ink-800 disabled:opacity-60"
          >
            {post.isPending ? t('comments.posting') : <><Send className="h-3.5 w-3.5" />{t('comments.post')}</>}
          </button>
        </div>
      </div>
    </section>
  );
}
